import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createCategory,
    updateCategory
} from '../../redux/features/categories/categorySlice';
import { AVAILABLE_COLORS } from './colors';
import './CategoryModal.css';

const CategoryModal = ({ category, onClose }) => {
    const dispatch = useDispatch();
    const { isLoading } = useSelector((state) => state.categories);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        color: AVAILABLE_COLORS[0],
        status: 'active'
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (category) {
            setFormData({
                title: category.title,
                description: category.description || '',
                color: category.color,
                status: category.status
            });
        }
    }, [category]);

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        } else if (formData.title.length > 50) {
            newErrors.title = 'Title must be less than 50 characters';
        }
        if (!formData.color) {
            newErrors.color = 'Please select a color';
        }
        if (formData.description && formData.description.length > 200) {
            newErrors.description = 'Description must be less than 200 characters';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleColorSelect = (color) => {
        setFormData(prev => ({
            ...prev,
            color
        }));
        if (errors.color) {
            setErrors(prev => ({
                ...prev,
                color: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            if (category) {
                await dispatch(updateCategory({ id: category._id, categoryData: formData })).unwrap();
            } else {
                await dispatch(createCategory(formData)).unwrap();
            }
            onClose();
        } catch (error) {
            console.error('Failed to save category:', error);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content category-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{category ? 'Edit Category' : 'Create Category'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label htmlFor="title">
                            Title <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter category title"
                            className={errors.title ? 'error' : ''}
                            maxLength={50}
                        />
                        {errors.title && <span className="error-text">{errors.title}</span>}
                        <span className="char-count">{formData.title.length}/50</span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Enter category description (optional)"
                            rows={3}
                            className={errors.description ? 'error' : ''}
                            maxLength={200}
                        />
                        {errors.description && <span className="error-text">{errors.description}</span>}
                        <span className="char-count">{formData.description.length}/200</span>
                    </div>

                    <div className="form-group">
                        <label>
                            Color <span className="required">*</span>
                        </label>
                        <div className="color-picker-grid">
                            {AVAILABLE_COLORS.map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    className={`color-option ${formData.color === color ? 'selected' : ''}`}
                                    style={{ backgroundColor: color }}
                                    onClick={() => handleColorSelect(color)}
                                    title={color}
                                >
                                    {formData.color === color && (
                                        <i className="fa fa-check"></i>
                                    )}
                                </button>
                            ))}
                        </div>
                        {errors.color && <span className="error-text">{errors.color}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="status">Status</label>
                        <div className="status-toggle">
                            <button
                                type="button"
                                className={`status-btn ${formData.status === 'active' ? 'active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, status: 'active' }))}
                            >
                                <i className="fa fa-check-circle"></i> Active
                            </button>
                            <button
                                type="button"
                                className={`status-btn ${formData.status === 'inactive' ? 'active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, status: 'inactive' }))}
                            >
                                <i className="fa fa-ban"></i> Inactive
                            </button>
                        </div>
                        <small className="help-text">
                            Inactive categories and their messages will be hidden from the message picker
                        </small>
                    </div>

                    {category && category.messageCount !== undefined && (
                        <div className="info-box">
                            <i className="fa fa-info-circle"></i>
                            <span>This category has <strong>{category.messageCount}</strong> message(s) linked to it</span>
                        </div>
                    )}

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn-save" disabled={isLoading}>
                            {isLoading ? 'Saving...' : category ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CategoryModal;
